import React, { useState, useEffect, useContext } from 'react';
import './Settings.css';
import AuthContext from '../../contexts/AuthContext';
import AuthService from '../../services/AuthService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const SettingsContent = ({ selectedSetting }) => {
    // State for Account Settings
    const navigate = useNavigate();
    const auth = useContext(AuthContext);
    const { user } = useContext(AuthContext);

    const [hasChanges, setHasChanges] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    //const [profileVisibility, setProfileVisibility] = useState('Public');
    const [github, setGithub] = useState('');
    const [linkedin, setLinkedin] = useState('');
    const [website, setWebsite] = useState('');
    const [errors, setErrors] = useState({});

    // State for Learning Preferences
    const [ignoreDifficulty, setIgnoreDifficulty] = useState(false);

    useEffect(() => {
        if (user) {
            const { username, email: userEmail, setting } = user;

            // Account Settings
            setName(username || '');
            setEmail(userEmail || '');
            //setProfileVisibility(setting?.profileVisibility || 'Public');
            setGithub(setting?.socialLinks?.github || '');
            setLinkedin(setting?.socialLinks?.linkedin || '');
            setWebsite(setting?.socialLinks?.website || '');

            // Learning Preferences
            setIgnoreDifficulty(setting?.ignoreDifficulty || false);
        }
    }, [user]);

    const checkForChanges = () => {
        if (!user) return false;

        const { username, email: userEmail, setting } = user;

        return (
            name !== (username || '') ||
            email !== (userEmail || '') ||
            password !== '' || // Only enable if a new password is entered
            confirmPassword !== '' || // Ensure confirmation is entered if password is changed
            //profileVisibility !== (setting?.profileVisibility || 'Public') ||
            github !== (setting?.socialLinks?.github || '') ||
            linkedin !== (setting?.socialLinks?.linkedin || '') ||
            website !== (setting?.socialLinks?.website || '') ||
            ignoreDifficulty !== (setting?.ignoreDifficulty || false)
        );
    };

    useEffect(() => {
        setHasChanges(checkForChanges());
    }, [name, email, password, github, linkedin, website, ignoreDifficulty]);


    const validateFields = () => {
        const newErrors = {};

        // Validate Name
        if (!name || name.length < 3) {
            newErrors.name = 'Display name must be at least 3 characters.';
        }

        // Validate Email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailRegex.test(email)) {
            newErrors.email = 'Please enter a valid email address.';
        }

        // Validate Password
        if (password && password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters.';
        }

        // Confirm Password
        if (password !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match.';
        }

        // Validate URLs
        const urlRegex = /^(https?:\/\/)?([\w-]+)+([\w-]+)+(\/[\w-]*)?$/;
        if (github && !urlRegex.test(github)) {
            newErrors.github = 'Please enter a valid GitHub URL.';
        }
        if (linkedin && !urlRegex.test(linkedin)) {
            newErrors.linkedin = 'Please enter a valid LinkedIn URL.';
        }
        if (website && !urlRegex.test(website)) {
            newErrors.website = 'Please enter a valid Website URL.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const saveAccountSettings = async () => {
        if (!validateFields()) {
            toast.error('Please fix the errors before saving.');
            return;
        }

        try {
            const requestData = {
                setting: {
                    username: name,
                    email,
                    password: password || undefined, // Only include if entered
                    //profileVisibility,
                    //socialLinks: {
                    //    github,
                    //    linkedin,
                    //    website,
                    //},
                },
            };

            const response = await fetch('/api/userdata/updateUserData', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', 
                body: JSON.stringify(requestData),
            });

            if (response.status === 200) {
                toast.success('Account settings saved successfully!');
                setPassword('');
                setConfirmPassword('');
                setHasChanges(false);

                user.username = name;
                user.email = email;
                //user.setting.profileVisibility = profileVisibility;
                
                AuthService.syncLocalUserSession(user);

            } else {
                throw new Error('Failed to save account settings');
            }
        } catch (error) {
            toast.error('Error saving account settings. Please try again.');
        }
    };

    // Function to save Learning Preferences
    const saveLearningPreferences = async () => {
        try {
            const requestData = {
                setting: {
                    ignoreDifficulty,
                },
            };

            const response = await fetch('/api/userdata/updateUserData', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', 
                body: JSON.stringify(requestData),
            });

            if (response.status === 200) {
                toast.error('Learning preferences saved successfully!');
                setHasChanges(false);

                user.setting.ignoreDifficulty = ignoreDifficulty;
                AuthService.syncLocalUserSession(user);
            } else {
                throw new Error('Failed to save learning preferences');
            }
        } catch (error) {
            console.error('Error saving learning preferences:', error.response?.data?.message || error.message);
            toast.error('Error saving learning preferences. Please try again.');
        }
    };

    const handleLogout = () => {
        // Clear client-side session and navigate immediately
        AuthService.removeUserSession();
        auth.setUser(null);
        navigate('/login', { replace: true });

        // Perform the logout request to the server in the background
        fetch('/api/auth/logout', {
            method: 'GET',
            credentials: 'include',
        }).catch((error) => {
            console.error('Error during logout request:', error.message);
        });
    };

    // Render Account Settings
    const renderAccountSettings = () => (
        <>
            <label>Display Name</label>
            <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your display name"
            />
            {errors.name && <p className="error-message">{errors.name}</p>}

            <label>Email Address</label>
            <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
            />
            {errors.email && <p className="error-message">{errors.email}</p>}

            <label>New Password</label>
            <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter a new password"
            />
            {errors.password && <p className="error-message">{errors.password}</p>}

            <label>Confirm Password</label>
            <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
            />
            {errors.confirmPassword && <p className="error-message">{errors.confirmPassword}</p>}

            <div className="align-right">
                <button className="btn btn-primary margin-btm-30" onClick={saveAccountSettings} disabled={!hasChanges}>
                    Save Changes
                </button>
            </div>
            <hr />
            <div className="align-center">
                <button className="logout-button margin-top-30" onClick={handleLogout}>
                    Log Out
                </button>
            </div>
        </>
    );

    // Render Learning Preferences
    const renderLearningPreferences = () => (
        <>
            <label>
                <input
                    type="checkbox"
                    checked={ignoreDifficulty}
                    onChange={() => setIgnoreDifficulty(!ignoreDifficulty)}
                />
                Ignore beginner topics
            </label>

            <div className="align-right">
                <button className="btn btn-primary margin-btm-30" onClick={saveLearningPreferences} disabled={!hasChanges}>
                    Save Changes
                </button>
            </div>
        </>
    );

    // Render based on selected setting
    const renderContent = () => {
        switch (selectedSetting) {
            case 'learning':
                return renderLearningPreferences();
            case 'account':
            default:
                return renderAccountSettings();
        }
    };

    return <div className="settings-content">{renderContent()}</div>;
};

export default SettingsContent;

            //<label>
            //    <input
            //        type="checkbox"
            //        checked={profileVisibility === 'Private'}
            //        onChange={() =>
            //            setProfileVisibility(profileVisibility === 'Public' ? 'Private' : 'Public')
            //        }
            //    />
            //    Profile Visibility
            //</label>

            //        <hr />
            //<label>GitHub</label>
            //<input
            //    type="url"
            //    value={github}
            //    onChange={(e) => setGithub(e.target.value)}
            //    placeholder="GitHub URL"
            ///>
            //<label>LinkedIn</label>
            //<input
            //    type="url"
            //    value={linkedin}
            //    onChange={(e) => setLinkedin(e.target.value)}
            //    placeholder="LinkedIn URL"
            ///>
            //<label>Website</label>
            //<input
            //    type="url"
            //    value={website}
            //    onChange={(e) => setWebsite(e.target.value)}
            //    placeholder="Website URL"
///>

            //            <label>Daily Goal (minutes)</label>
            //<input
            //    type="number"
            //    min="1"
            //    value={dailyGoal}
            //    onChange={(e) => setDailyGoal(Number(e.target.value))}
            //    placeholder="Enter your daily goal"
///>

